import { useState, useEffect, useCallback } from 'react';
import { getProductOffers, ProductOffer } from '@/services/seller/discount/discount-offers';
import { getKoreanDate } from '@/lib/dateUtils';

interface UseProductOffersOptions {
  restaurantId: string;
  offerType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT';
  enabled?: boolean;
}

interface UseProductOffersReturn {
  offers: ProductOffer[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isProductRegistered: (productId: string) => boolean;
  getProductOffer: (productId: string) => ProductOffer | undefined;
}

export function useProductOffers({
  restaurantId,
  offerType,
  enabled = true
}: UseProductOffersOptions): UseProductOffersReturn {
  const [offers, setOffers] = useState<ProductOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOffers = useCallback(async () => {
    if (!restaurantId || !enabled) return;

    try {
      setLoading(true);
      setError(null);
      
      
      const result = await getProductOffers(restaurantId, offerType);
      
      
      if (result.success && result.data) {
        setOffers(result.data);
      } else {
        setError(result.error || '할인 정보를 불러오는데 실패했습니다');
      }
    } catch (err) {
      console.error('Failed to fetch product offers:', err);
      setError('할인 정보를 불러오는 중 오류가 발생했습니다');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, offerType, enabled]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const isProductRegistered = useCallback((productId: string): boolean => {
    const offer = offers.find(o => o.product_id === productId);
    
    
    if (!offer) {
      return false;
    }
    
    // 마감할인의 경우 당일 valid_from 확인
    if (offerType === 'DEADLINE_DISCOUNT' && offer.valid_from) {
      const today = getKoreanDate();
      const offerDate = offer.valid_from.split('T')[0];
      const todayDate = today.split('T')[0];
      
      
      return offerDate === todayDate && offer.is_current === true;
    }
    
    // 예약할인의 경우 is_current만 확인
    const result = offer.is_current === true;
    return result;
  }, [offers, offerType]);

  const getProductOffer = useCallback((productId: string): ProductOffer | undefined => {
    return offers.find(o => o.product_id === productId);
  }, [offers]);

  return {
    offers,
    loading,
    error,
    refetch: fetchOffers,
    isProductRegistered,
    getProductOffer
  };
}